// src/components/continuousLegend.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from '../theme/ThemeContext';
import * as d3 from 'd3';

const ContinuousLegend = ({ values, scatterColorCategory, styles, onRangeSelect, scatterplot, ids }) => {
    const svgRef = useRef();
    const { theme } = useTheme();
    const [selectionRange, setSelectionRange] = useState(null);

    const selectionState = useRef({
        isSelecting: false,
        startX: null,
        selectionRect: null
    });

    const colorSchemes = {
        'shannon_index_e': d3.interpolateViridis,
        'simpson_index': d3.interpolateInferno,
        'richnes': (t) => {
            const powerScale = d3.scalePow()
                .exponent(0.3)
                .domain([0, 1])
                .range([0, 1]);
            return d3.interpolateYlGnBu(powerScale(t));
        },
        'chao1': (t) => {
            const powerScale = d3.scalePow()
                .exponent(0.3)
                .domain([0, 1])
                .range([0, 1]);
            return d3.interpolateYlOrRd(powerScale(t));
        },
        'confidence': d3.interpolateViridis,
        'unknownness_score': d3.interpolateTurbo
    };

    const nameConvDict = {
        'shannon_index_e': 'Shannon Index',
        'simpson_index': 'Simpson index',
        'richnes': 'Richness',
        'richness': 'Richness',
        'chao1': 'Chao1',
        'confidence': 'mean nearest neighbor',
        'unknownness_score': 'Unknownness',
    }

    const legendStyle = {
        ...styles,
        bottom: '20px',
        left: '20px',
        padding: '15px',
        borderRadius: '8px',
        userSelect: 'none'
    };

    const selectSamplesInRange = useCallback((minVal, maxVal) => {
        if (!values || !scatterplot || !ids) return;

        const selectedIndices = [];
        values.forEach((value, index) => {
            if (value >= minVal && value <= maxVal) {
                selectedIndices.push(index);
            }
        });

        if (selectedIndices.length > 0) {
            scatterplot.select(selectedIndices);
        }

        if (onRangeSelect) {
            onRangeSelect(minVal, maxVal, selectedIndices.length);
        }
    }, [values, scatterplot, ids, onRangeSelect]);

    const resetSelection = useCallback(() => {
        if (selectionState.current.selectionRect) {
            selectionState.current.selectionRect.remove();
        }
        selectionState.current = {
            isSelecting: false,
            startX: null,
            selectionRect: null
        };
        setSelectionRange(null);
    }, []);

    useEffect(() => {
        if (!values || values.length === 0) return;

        const gradientHeight = 25;
        const width = 400;
        const height = 80;
        const margin = { top: 20, right: 20, bottom: 20, left: 20 };

        // Clear previous content
        d3.select(svgRef.current).selectAll("*").remove();

        // Reset selection state when re-rendering
        resetSelection();

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        const gradient = svg.append("defs")
            .append("linearGradient")
            .attr("id", `gradient-${scatterColorCategory}`)
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%");

        const colorScale = d3.scaleSequential()
            .domain([0, 1])
            .interpolator(colorSchemes[scatterColorCategory] || d3.interpolateViridis);

        const stops = d3.range(0, 1.1, 0.1);
        stops.forEach(stop => {
            gradient.append("stop")
                .attr("offset", `${stop * 100}%`)
                .attr("stop-color", colorScale(stop));
        });

        // gradient bar
        const gradientRect = svg.append("rect")
            .attr("x", margin.left)
            .attr("y", margin.top)
            .attr("width", width - margin.left - margin.right)
            .attr("height", gradientHeight)
            .style("fill", `url(#gradient-${scatterColorCategory})`)
            .style("cursor", "crosshair");

        // scale
        let minVal = d3.min(values);
        let maxVal = d3.max(values);

        // Force 0-1 range for confidence values
        if (scatterColorCategory === 'confidence') {
            minVal = 0;
            maxVal = 1;
        }

        const xScale = d3.scaleLinear()
            .domain([minVal, maxVal])
            .range([margin.left, width - margin.right]);

        const xAxis = d3.axisBottom(xScale)
            .ticks(5)
            .tickFormat(d3.format(".1f"));

        svg.append("g")
            .attr("transform", `translate(0,${margin.top + gradientHeight})`)
            .call(xAxis)
            .style("color", theme.colors.text)
            .style("font-size", "12px");

        // title
        svg.append("text")
            .attr("x", width / 2)
            .attr("y", gradientHeight / 2)
            .attr("text-anchor", "middle")
            .style("fill", theme.colors.text)
            .style("font-size", "14px")
            .text(nameConvDict[scatterColorCategory]);

        // Selection overlay group
        const selectionGroup = svg.append("g")
            .attr("class", "selection-group");

        // Mouse event handlers
        const handleMouseDown = (event) => {
            event.preventDefault();
            const [mouseX] = d3.pointer(event);
            const gradientLeft = margin.left;
            const gradientRight = width - margin.right;

            if (mouseX >= gradientLeft && mouseX <= gradientRight) {
                // Reset any previous selection
                resetSelection();

                selectionState.current.startX = mouseX;
                selectionState.current.isSelecting = true;

                selectionState.current.selectionRect = selectionGroup.append("rect")
                    .attr("x", mouseX)
                    .attr("y", margin.top)
                    .attr("width", 0)
                    .attr("height", gradientHeight)
                    .style("fill", "rgba(255, 255, 255, 0.3)")
                    .style("stroke", theme.colors.text)
                    .style("stroke-width", 2)
                    .style("stroke-dasharray", "3,3");
            }
        };

        const handleMouseMove = (event) => {
            if (!selectionState.current.isSelecting || !selectionState.current.selectionRect || selectionState.current.startX === null) {
                return;
            }

            const [mouseX] = d3.pointer(event);
            const gradientLeft = margin.left;
            const gradientRight = width - margin.right;
            const clampedX = Math.max(gradientLeft, Math.min(gradientRight, mouseX));

            const rectX = Math.min(selectionState.current.startX, clampedX);
            const rectWidth = Math.abs(clampedX - selectionState.current.startX);

            selectionState.current.selectionRect
                .attr("x", rectX)
                .attr("width", rectWidth);

            // Update selection range for display
            const minX = Math.min(selectionState.current.startX, clampedX);
            const maxX = Math.max(selectionState.current.startX, clampedX);
            const minValue = xScale.invert(minX);
            const maxValue = xScale.invert(maxX);

            setSelectionRange({ min: minValue, max: maxValue });
        };

        const handleMouseUp = (event) => {
            if (!selectionState.current.isSelecting || selectionState.current.startX === null) {
                return;
            }

            const [mouseX] = d3.pointer(event);
            const gradientLeft = margin.left;
            const gradientRight = width - margin.right;
            const clampedX = Math.max(gradientLeft, Math.min(gradientRight, mouseX));

            const minX = Math.min(selectionState.current.startX, clampedX);
            const maxX = Math.max(selectionState.current.startX, clampedX);
            const minValue = xScale.invert(minX);
            const maxValue = xScale.invert(maxX);

            // Only select if there's a meaningful range (minimum 3 pixels)
            if (Math.abs(maxX - minX) >= 3) {
                selectSamplesInRange(minValue, maxValue);
            }

            // Reset selection state
            resetSelection();
        };

        const handleMouseLeave = () => {
            if (selectionState.current.isSelecting) {
                resetSelection();
            }
        };

        // Add event listeners to the entire SVG
        svg.on("mousedown", handleMouseDown)
            .on("mousemove", handleMouseMove)
            .on("mouseup", handleMouseUp)
            .on("mouseleave", handleMouseLeave);

        // Also add global mouse up listener to handle mouse up outside the SVG
        const handleGlobalMouseUp = () => {
            if (selectionState.current.isSelecting) {
                resetSelection();
            }
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);

        // Cleanup function
        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            resetSelection();
        };

    }, [values, theme, selectSamplesInRange, scatterColorCategory, resetSelection]);

    return (
        <div style={legendStyle}>
            <svg ref={svgRef}></svg>
            {selectionRange && (
                <div style={{
                    marginTop: '5px',
                    fontSize: '12px',
                    color: theme.colors.text,
                    textAlign: 'center'
                }}>
                    {/* Range: {selectionRange.min.toFixed(2)} - {selectionRange.max.toFixed(2)} */}
                </div>
            )}
            <div style={{
                marginTop: '5px',
                fontSize: '11px',
                color: theme.colors.text,
                textAlign: 'center',
                opacity: 0.7
            }}>
                Click and drag to select range
            </div>
        </div>
    );
};

export default ContinuousLegend;